import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Form,
  DatePicker,
  Select,
  InputNumber,
  Input,
  Collapse,
  Button,
  Row,
  Col,
  Space,
  Badge,
  Typography,
  Divider,
  Card
} from 'antd';
import {
  FilterOutlined,
  ClearOutlined,
  DownOutlined,
  SearchOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  BankOutlined
} from '@ant-design/icons';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';

const { Text } = Typography;

/**
 * StandardFilter - A highly reusable filter component
 * 
 * @param {Object} props
 * @param {Array} props.filters - Array of filter configurations
 * @param {Function} props.onFilterChange - Callback when filters change
 * @param {Function} props.onApplyFilters - Callback when apply filters is clicked
 * @param {Function} props.onClearFilters - Callback when clear filters is clicked
 * @param {Object} props.initialValues - Initial filter values
 * @param {boolean} props.loading - Loading state for apply button
 * @param {boolean} props.collapsible - Whether to show filters in collapsible panel
 * @param {boolean} props.defaultExpanded - Whether collapsible panel is expanded by default
 * @param {string} props.size - Size of form components ('small', 'middle', 'large')
 * @param {Object} props.style - Custom styles
 * @param {string} props.title - Title for the filter section
 * @param {boolean} props.showActiveCount - Whether to show active filter count
 * @param {boolean} props.showSearchIcon - Whether to show search icon in title
 */
const StandardFilter = ({
  filters = [],
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  initialValues = {},
  loading = false,
  collapsible = true,
  defaultExpanded = false,
  size = 'middle',
  style = {},
  title = 'Advanced Filters',
  showActiveCount = true,
  showSearchIcon = true
}) => {
  const [form] = Form.useForm();
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [filterValues, setFilterValues] = useState(initialValues);

  // Calculate active filters count
  const calculateActiveFilters = useCallback((values) => {
    return Object.values(values).filter(value => 
      value !== null && 
      value !== undefined && 
      value !== '' && 
      !(Array.isArray(value) && value.length === 0)
    ).length;
  }, []);

  // Handle form values change
  const handleValuesChange = useCallback((changedValues, allValues) => {
    const newValues = { ...filterValues, ...changedValues };
    setFilterValues(newValues);
    setActiveFiltersCount(calculateActiveFilters(newValues));
    
    if (onFilterChange) {
      onFilterChange(changedValues, newValues);
    }
  }, [filterValues, onFilterChange, calculateActiveFilters]);

  // Handle apply filters
  const handleApplyFilters = useCallback(() => {
    if (onApplyFilters) {
      onApplyFilters(filterValues);
    }
  }, [filterValues, onApplyFilters]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    form.resetFields();
    setFilterValues({});
    setActiveFiltersCount(0);
    
    if (onClearFilters) {
      onClearFilters();
    }
  }, [form, onClearFilters]);

  // Update form when initial values change
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      setFilterValues(initialValues);
      setActiveFiltersCount(calculateActiveFilters(initialValues));
    }
  }, [initialValues, form, calculateActiveFilters]);

  // Render individual filter component
  const renderFilterComponent = useCallback((filter) => {
    const { type, name, label, placeholder, options, ...props } = filter;

    const commonProps = {
      placeholder: placeholder || `Select ${label}`,
      size,
      style: { width: '100%' },
      ...props
    };

    switch (type) {
      case 'search':
        return (
          <Input
            {...commonProps}
            prefix={<SearchOutlined />}
            allowClear
          />
        );

      case 'date':
        return (
          <DatePicker
            {...commonProps}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
          />
        );

      case 'dateRange':
        return (
          <DatePicker.RangePicker
            {...commonProps}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
          />
        );

      case 'select':
        return (
          <Select
            {...commonProps}
            allowClear
            showSearch
            filterOption={props.filterOption !== false ? (input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            : false}
            onSearch={props.onSearch}
            notFoundContent={props.notFoundContent}
            options={options}
          />
        );

      case 'number':
        return (
          <InputNumber
            {...commonProps}
            min={0}
            step={props.step || 1}
            style={{ width: '100%' }}
          />
        );

      case 'currency':
        return (
          <InputNumber
            {...commonProps}
            min={0}
            step={props.step || 100}
            formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value.replace(/₦\s?|(,*)/g, '')}
            style={{ width: '100%' }}
          />
        );

      case 'month':
        return (
          <Select
            {...commonProps}
            allowClear
            options={[
              { label: 'January', value: 1 },
              { label: 'February', value: 2 },
              { label: 'March', value: 3 },
              { label: 'April', value: 4 },
              { label: 'May', value: 5 },
              { label: 'June', value: 6 },
              { label: 'July', value: 7 },
              { label: 'August', value: 8 },
              { label: 'September', value: 9 },
              { label: 'October', value: 10 },
              { label: 'November', value: 11 },
              { label: 'December', value: 12 }
            ]}
          />
        );

      case 'year':
        return (
          <Select
            {...commonProps}
            allowClear
            options={Array.from({ length: 25 }, (_, i) => ({
              label: (2024 - i).toString(),
              value: 2024 - i
            }))}
          />
        );

      default:
        return (
          <Input
            {...commonProps}
            allowClear
          />
        );
    }
  }, [size]);

  // Get icon for filter type
  const getFilterIcon = useCallback((type) => {
    switch (type) {
      case 'search': return <SearchOutlined />;
      case 'date':
      case 'dateRange': return <CalendarOutlined />;
      case 'currency':
      case 'number': return <DollarOutlined />;
      case 'select': return <UserOutlined />;
      default: return <FilterOutlined />;
    }
  }, []);

  // Render filter form
  const renderFilterForm = () => (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      style={{ 
        marginTop: collapsible ? '0' : '0',
        padding: collapsible ? '0 24px' : '0'
      }}
    >
      <Row gutter={[20, 20]}>
        {filters.map((filter, index) => (
          <Col 
            key={filter.name || index}
            xs={24} 
            sm={filter.colSpan?.xs || 12} 
            md={filter.colSpan?.md || 6}
            lg={filter.colSpan?.lg || 6}
            xl={filter.colSpan?.xl || 6}
          >
            <Form.Item 
              label={
                <div className="filter-label" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <div className="filter-label-icon" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    background: 'var(--ant-primary-color-light, #e6f2ff)',
                    color: 'var(--ant-primary-color, #1677ff)',
                    fontSize: '12px'
                  }}>
                    {getFilterIcon(filter.type)}
                  </div>
                  <span style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    color: 'var(--ant-text-color, #1e293b)',
                    letterSpacing: '-0.01em'
                  }}>
                    {filter.label}
                  </span>
                </div>
              } 
              name={filter.name}
              rules={filter.rules}
              style={{ marginBottom: '0' }}
            >
              {renderFilterComponent(filter)}
            </Form.Item>
          </Col>
        ))}
      </Row>
      
      <Divider style={{ 
        margin: '24px 0 20px 0',
        borderColor: 'var(--ant-border-color-split, #f0f0f0)'
      }} />
      
      <Row justify="space-between" align="middle">
        <Col>
          <Space size="middle">
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleApplyFilters}
              loading={loading}
              size={size}
              style={{
                borderRadius: '10px',
                height: size === 'small' ? '36px' : size === 'large' ? '48px' : '42px',
                padding: '0 28px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, var(--ant-primary-color, #1677ff) 0%, var(--ant-primary-color-hover, #1665d8) 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(22, 119, 255, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(22, 119, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(22, 119, 255, 0.3)';
              }}
            >
              Apply Filters
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              size={size}
              style={{
                borderRadius: '10px',
                height: size === 'small' ? '36px' : size === 'large' ? '48px' : '42px',
                padding: '0 28px',
                fontWeight: 500,
                border: '1px solid var(--ant-border-color, #e1e5e9)',
                color: 'var(--ant-text-color, #1e293b)',
                background: 'var(--ant-component-background, #ffffff)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = 'var(--ant-primary-color, #1677ff)';
                e.target.style.color = 'var(--ant-primary-color, #1677ff)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = 'var(--ant-border-color, #e1e5e9)';
                e.target.style.color = 'var(--ant-text-color, #1e293b)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Clear All
            </Button>
          </Space>
        </Col>
        {showActiveCount && (
          <Col>
            <div className="active-filters-info" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: 'var(--ant-primary-color-light, #e6f2ff)',
              borderRadius: '20px',
              border: '1px solid var(--ant-primary-color-border, #b3d9ff)'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--ant-primary-color, #1677ff)'
              }} />
              <Text style={{ 
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--ant-primary-color, #1677ff)'
              }}>
                {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
              </Text>
            </div>
          </Col>
        )}
      </Row>
    </Form>
  );

  // Render collapsible version
  if (collapsible) {
    return (
      <div className="standard-filter-container modern-filter" style={style}>
        <Collapse
          ghost
          defaultActiveKey={defaultExpanded ? ['filters'] : []}
          expandIcon={({ isActive }) => (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'var(--ant-primary-color-light, #e6f2ff)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <DownOutlined 
                rotate={isActive ? 180 : 0} 
                style={{ 
                  color: 'var(--ant-primary-color, #1677ff)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }} 
              />
            </div>
          )}
          style={{
            background: 'transparent',
            border: 'none'
          }}
          items={[
            {
              key: 'filters',
              label: (
                <div className="filter-header" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '8px 0',
                  transition: 'all 0.3s ease'
                }}>
                  {showSearchIcon && (
                    <div className="filter-icon-wrapper" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, var(--ant-primary-color, #1677ff) 0%, var(--ant-primary-color-hover, #1665d8) 100%)',
                      boxShadow: '0 2px 8px rgba(22, 119, 255, 0.2)',
                      transition: 'all 0.3s ease'
                    }}>
                      <FilterOutlined style={{ 
                        color: 'white', 
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }} />
                    </div>
                  )}
                  <div className="filter-title-section">
                    <span className="filter-title" style={{ 
                      fontWeight: 600, 
                      fontSize: '16px',
                      color: 'var(--ant-text-color, #1e293b)',
                      letterSpacing: '-0.01em'
                    }}>
                      {title}
                    </span>
                    {showActiveCount && activeFiltersCount > 0 && (
                      <div style={{ marginTop: '2px' }}>
                        <Badge 
                          count={activeFiltersCount} 
                          style={{ 
                            backgroundColor: 'var(--ant-primary-color, #1677ff)',
                            boxShadow: '0 2px 4px rgba(22, 119, 255, 0.3)',
                            fontSize: '11px',
                            fontWeight: '600',
                            minWidth: '18px',
                            height: '18px',
                            lineHeight: '16px'
                          }} 
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              children: (
                <div className="filter-content" style={{
                  padding: '16px 0',
                  background: 'var(--ant-component-background, #ffffff)',
                  borderRadius: '12px',
                  border: '1px solid var(--ant-border-color, #e1e5e9)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  marginTop: '8px'
                }}>
                  {renderFilterForm()}
                </div>
              )
            }
          ]}
        />
      </div>
    );
  }

  // Render non-collapsible version
  return (
    <Card 
      className="standard-filter-container modern-filter" 
      style={{ 
        marginBottom: '24px', 
        borderRadius: '16px',
        border: '1px solid var(--ant-border-color, #e1e5e9)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        background: 'var(--ant-component-background, #ffffff)',
        overflow: 'hidden',
        ...style 
      }}
      title={
        <div className="filter-header" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          padding: '8px 0'
        }}>
          {showSearchIcon && (
            <div className="filter-icon-wrapper" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, var(--ant-primary-color, #1677ff) 0%, var(--ant-primary-color-hover, #1665d8) 100%)',
              boxShadow: '0 2px 8px rgba(22, 119, 255, 0.2)'
            }}>
              <FilterOutlined style={{ 
                color: 'white', 
                fontSize: '14px',
                fontWeight: 'bold'
              }} />
            </div>
          )}
          <div className="filter-title-section">
            <span className="filter-title" style={{ 
              fontWeight: 600, 
              fontSize: '16px',
              color: 'var(--ant-text-color, #1e293b)',
              letterSpacing: '-0.01em'
            }}>
              {title}
            </span>
            {showActiveCount && activeFiltersCount > 0 && (
              <div style={{ marginTop: '2px' }}>
                <Badge 
                  count={activeFiltersCount} 
                  style={{ 
                    backgroundColor: 'var(--ant-primary-color, #1677ff)',
                    boxShadow: '0 2px 4px rgba(22, 119, 255, 0.3)',
                    fontSize: '11px',
                    fontWeight: '600',
                    minWidth: '18px',
                    height: '18px',
                    lineHeight: '16px'
                  }} 
                />
              </div>
            )}
          </div>
        </div>
      }
    >
      {renderFilterForm()}
    </Card>
  );
};

export default StandardFilter;

